// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — bazaar.js  (THE BOARDWALK BAZAAR ENGINE)
// ────────────────────────────────────────────────────────────────────
// The player marketplace. Pierre's truest purpose: the ONE place in the
// IE universe where players freely buy and sell — SAFELY — because the
// Pier is the exchange authority. Same law as the Crest Well, extended
// from currency↔currency to player↔player.
//
// ── WHAT LIVES HERE ──────────────────────────────────────────────────
//   • MINTING   — a creator mints an EDITION of an artwork: N copies,
//                 each stamped with a unique, verifiable Sigil Code
//                 (◆IE·SERIAL·copy/size). Edition size is IMMUTABLE at
//                 mint — scarcity is a promise, not a suggestion.
//   • LISTINGS  — true escrow. A listed piece LEAVES the seller's
//                 gallery and lives inside the listing until it sells
//                 or is delisted. No double-sells, no phantom copies.
//   • BUYING    — dual-currency (◆ DC or ◈ SE, seller's choice at
//                 listing time). Pierre takes the same 5% cut as the
//                 Crest Well. All currency moves are delta-based
//                 runTransaction — never read-compute-write.
//   • PROVENANCE— every piece remembers where it came from (the
//                 Provenance Law). earned > minted > bazaar radiance.
//   • GALLERY   — profiles/{uid}/gallery/* — the pieces you own. This
//                 is what the Hall of Deeds' picture frames will read
//                 in the frames phase (M1).
//
// ── CURRENCY LANE LAW (why this file is legal) ───────────────────────
// Every IE title writes only its OWN currency. PIERRE is the exchange
// authority — the only app allowed to move ALL of them, and only
// inside its own walls. The Bazaar is Pierre-side code moving currency
// between two players at the Pier. Same authority the Crest Well
// already exercises. No other IE app may ever import this file's
// buy/sell functions.
//
// ── EVERY CURRENCY IS WELCOME (2026-07-28) ───────────────────────────
// A seller may price a piece in ANY currency registered at the Crest
// Well. Chocolate Coins, Snake Eyes Coins, Divine Crests, and whatever
// the next title mints — they all show up in the price picker the day
// their rate row goes live, with zero code changes here.
//
// A buyer must HOLD the currency a listing asks for. We do not
// auto-route payment through DC at checkout: that would be a silent
// Crest Well exchange that skips the 10-minute cooldown, and the
// cooldown is the only thing standing between the Well and a free
// arbitrage machine. Want to buy an SE listing with CC? Walk to the
// Well like everyone else.
//
// ── NO CUT (2026-07-28) ──────────────────────────────────────────────
// The Bazaar's 5% cut is RETIRED, along with pierre/bazaar/treasury.
// It only ever existed because it copied the Crest Well's fee, and
// that fee is retired canon. The seller receives the full asking
// price. `ledger.cut` stays in the schema for rows already written —
// append-only law, we never rewrite history — and is written as 0
// forever.
//
// ── FIREBASE PATHS (whitelist with the code) ─────────────────────────
//   bazaar/mints/{artId}              — the edition record (immutable)
//   bazaar/listings/{listingId}       — escrowed listings
//   bazaar/ledger/{saleId}            — every completed sale, forever
//   profiles/{uid}/gallery/{pieceId}  — owned pieces (append/remove via
//                                       escrow only; never overwritten)
// Beta rules are `auth != null` so these work as-is today; they're on
// the hardening list for the rules-tightening session.
// ════════════════════════════════════════════════════════════════════

import {
  ref, get, set, update, onValue, runTransaction, push,
} from "firebase/database";
import { db } from "./firebase";
import { CURRENCIES, DC_ID, tintFor, getRates } from "./progression";
import { isAcquireOnly } from "./upDawgs";

// ─── LAWS & CONSTANTS ────────────────────────────────────────────────
// NOTE: BAZAAR_CUT is deliberately gone. Do not reintroduce it. See the
// "NO CUT" note in the header. If you find code importing it, that code
// predates 2026-07-28 and needs updating, not a shim.

export const MAX_EDITION_SIZE = 100; // per mint. Bump-anytime constant.
export const MIN_EDITION_SIZE = 1;   // a 1/1 is legal and glorious.

// ─── CURRENCIES A SELLER MAY ASK FOR ─────────────────────────────────
// Derived live from the Crest Well's rate table, NOT hardcoded. Every
// currency registered at the Well is legal tender at the Bazaar the
// moment its rate row goes live — no deploy, no code change here.
//
// `field` is the profiles/{uid} leaf the money actually lives in, and
// it comes from the CURRENCIES registry rather than the rate table, so
// a listing priced in a currency that later goes INACTIVE can still be
// bought and delisted. Deactivating a currency must never strand a
// player's piece in escrow forever.

export const bazaarCurrencies = (rates) => {
  const out = {};
  for (const id of [DC_ID, ...Object.keys(rates || {})]) {
    const meta = CURRENCIES[id];
    if (!meta?.path) continue;
    // ⭐ THE ACQUIRE-ONLY LAW, AT THE FOURTH DOOR (PIERRE_PRIME L42).
    // ⚠ UP Dawgs were kept out of here only by ACCIDENT — `CURRENCIES`
    // simply had no UD row, so the `!meta?.path` line above dropped
    // them. The day anyone finishes registering UD to the standard the
    // registry law describes ("one row plus one rate row"), a
    // player-to-player market in Dawgs opens by itself and a timer-fed
    // faucet starts buying finite minted art. A law that holds because
    // of a missing row is not holding. Now it is stated.
    // Found by adversarial review 2026-09-14.
    if (isAcquireOnly(id)) continue;
    out[id] = {
      field: meta.path,
      glyph: meta.icon,
      label: meta.name,
      color: tintFor(id),
      provisional: !!rates?.[id]?.provisional,
    };
  }
  return out;
};

// Resolve one currency for a WRITE. Registry-only — never gated on the
// rate table, for the stranded-escrow reason above.
export const currencyField = (id) => CURRENCIES[id]?.path || null;

// Price bounds, derived from the currency's rate so they scale with
// whatever the next title mints instead of needing a new hardcoded row.
// DC is the rare one, so it gets tight bounds. These are dials.
export const priceBoundsFor = (id, rates) => {
  if (id === DC_ID) return { min: 1, max: 100000 };
  const perCrest = Number(rates?.[id]?.perCrest) || 1000;
  return {
    min: Math.max(1, Math.round(perCrest / 100)),  // ~1/100th of a Crest
    max: perCrest * 100000,
  };
};

// Categories. `art` is live. Music and books are DECLARED — the schema
// carries them from day one (a listing is a listing) — but locked until
// their own phase ships (media hosting + player experience).
export const BAZAAR_CATEGORIES = {
  art:   { label: "Art & Prints", icon: "🖼️", live: true  },
  music: { label: "Music",        icon: "🎵", live: false },
  books: { label: "Books",        icon: "📖", live: false },
};

// v1 image sources: the IE CDN only. Uploads (Firebase Storage +
// moderation) are phase M3 — for now caP'n curates what reaches the CDN,
// which IS the moderation layer.
export const APPROVED_IMAGE_HOSTS = ["terror-assets.pages.dev"];

export const isApprovedImage = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "https:" && APPROVED_IMAGE_HOSTS.some(
      (h) => u.hostname === h
    );
  } catch {
    return false;
  }
};

// ─── PROVENANCE (the Provenance Law) ─────────────────────────────────
// Every piece remembers where it came from. Earned glows brighter than
// bought. Radiance mirrors SourceSigil's ladder (0 flat → 3 radiant).
export const PIECE_PROVENANCE = {
  earned: { label: "Earned — a Deed made Physical", radiance: 3, color: "#ffd24a" },
  minted: { label: "Minted by its Creator",          radiance: 2, color: "#a78bfa" },
  bazaar: { label: "Bought at the Boardwalk Bazaar", radiance: 1, color: "#7fd8ff" },
};

// ─── SIGIL CODES ─────────────────────────────────────────────────────
// Every copy of every mint carries a unique, human-readable, verifiable
// code:   ◆IE·K7X2M9·3/25
// SERIAL is the mint's artId serial (uppercase base36 — time + entropy,
// unguessable enough, short enough to read aloud). copy/size is that
// copy's place in the edition. Verification = look up the mint record;
// if the serial exists, the creator/edition/title are the mint's truth.

const newSerial = () => (
  Date.now().toString(36) + Math.floor(Math.random() * 46656).toString(36).padStart(3, "0")
).toUpperCase();

export const artIdFromSerial = (serial) => `art_${serial.toLowerCase()}`;
export const serialFromArtId = (artId)  => artId.replace(/^art_/, "").toUpperCase();

export const pieceCode = (artId, copyNo, editionSize) =>
  `◆IE·${serialFromArtId(artId)}·${copyNo}/${editionSize}`;

export const pieceIdFor = (artId, copyNo) => `${artId}_c${copyNo}`;

// Parse "◆IE·K7X2M9·3/25" (glyph optional, case-insensitive) →
// { artId, copyNo, editionSize } or null.
export const parseCode = (raw) => {
  const m = String(raw).trim().toUpperCase().match(
    /^◆?IE[·.\-\s]([A-Z0-9]+)[·.\-\s](\d+)\s*\/\s*(\d+)$/
  );
  if (!m) return null;
  return {
    artId: artIdFromSerial(m[1]),
    copyNo: parseInt(m[2], 10),
    editionSize: parseInt(m[3], 10),
  };
};

// Verify a code against the chain of truth (the mint record).
// Returns { valid, mint?, copyNo?, reason? }.
export const verifyCode = async (raw) => {
  const parsed = parseCode(raw);
  if (!parsed) return { valid: false, reason: "That's not a Sigil Code." };
  const snap = await get(ref(db, `bazaar/mints/${parsed.artId}`));
  if (!snap.exists()) return { valid: false, reason: "No mint bears this serial." };
  const mint = snap.val();
  if (parsed.editionSize !== mint.editionSize || parsed.copyNo < 1 || parsed.copyNo > mint.editionSize) {
    return { valid: false, reason: "Serial is real, but the edition numbers don't match its mint." };
  }
  return { valid: true, mint: { ...mint, artId: parsed.artId }, copyNo: parsed.copyNo };
};

// ─── MINTING ─────────────────────────────────────────────────────────
// Creator mints an edition: ONE immutable mint record + N pieces placed
// straight into the creator's gallery with provenance "minted".
// Grant-before-flag: pieces are written before anything else can
// reference them. Edition size can never be raised after mint.
export const mintEdition = async (uid, creatorName, opts = {}) => {
  const {
    title = "", desc = "", image = "",
    category = "art", editionSize = 1,
  } = opts;

  const cat = BAZAAR_CATEGORIES[category];
  if (!cat)      return { success: false, reason: "Unknown category." };
  if (!cat.live) return { success: false, reason: `${cat.label} minting isn't open yet.` };

  const cleanTitle = String(title).trim().slice(0, 60);
  if (cleanTitle.length < 2) return { success: false, reason: "Give it a title (2+ characters)." };

  const size = Math.floor(Number(editionSize));
  if (!Number.isFinite(size) || size < MIN_EDITION_SIZE || size > MAX_EDITION_SIZE) {
    return { success: false, reason: `Edition size must be ${MIN_EDITION_SIZE}–${MAX_EDITION_SIZE}.` };
  }

  if (!isApprovedImage(image)) {
    return {
      success: false,
      reason: "v1 art must live on the IE CDN (terror-assets.pages.dev). Player uploads come in a later phase.",
    };
  }

  const serial = newSerial();
  const artId  = artIdFromSerial(serial);
  const now    = Date.now();

  const mint = {
    creator: uid,
    creatorName: String(creatorName || "Unknown").slice(0, 24),
    title: cleanTitle,
    desc: String(desc).trim().slice(0, 300),
    image,
    category,
    editionSize: size,
    mintedAt: now,
  };

  // 1) The immutable mint record — the chain of truth for every code.
  await set(ref(db, `bazaar/mints/${artId}`), mint);

  // 2) All copies into the creator's gallery (one update, all-or-nothing).
  const galleryWrites = {};
  for (let c = 1; c <= size; c++) {
    galleryWrites[`profiles/${uid}/gallery/${pieceIdFor(artId, c)}`] = {
      artId,
      copyNo: c,
      code: pieceCode(artId, c, size),
      provenance: "minted",
      acquiredAt: now,
    };
  }
  await update(ref(db), galleryWrites);

  return { success: true, artId, serial, editionSize: size };
};

// ─── EARNED PRINTS (the seam — no live grants yet) ───────────────────
// Deeds made physical: system art granted by achievements/events, with
// provenance "earned" (radiance 3 — outglows anything bought). These are
// SYSTEM mints (creator "pierre"), open editions. NOTE: like cosmetics,
// nothing fires this yet — the registry exists so the achievement layer
// can call grantEarnedPrint later without a schema change.
export const EARNED_PRINTS = {
  // print_pier_dawn: { title: "Dawn Over the Pier", image: cdnUrl(...), grantedBy: "pier_first_visit" },
};

export const grantEarnedPrint = async (uid, printId) => {
  const print = EARNED_PRINTS[printId];
  if (!print) return { success: false, reason: "Unknown earned print." };
  const now = Date.now();
  const pieceId = `earned_${printId}`;
  const snap = await get(ref(db, `profiles/${uid}/gallery/${pieceId}`));
  if (snap.exists()) return { success: false, reason: "Already granted." };
  await set(ref(db, `profiles/${uid}/gallery/${pieceId}`), {
    artId: pieceId,
    copyNo: 0,
    code: `◆IE·EARNED·${printId.toUpperCase()}`,
    provenance: "earned",
    acquiredAt: now,
    printId,
  });
  return { success: true };
};

// ─── GALLERY (what you own) ──────────────────────────────────────────
export const listenToGallery = (uid, callback) =>
  onValue(ref(db, `profiles/${uid}/gallery`), (snap) => {
    if (!snap.exists()) return callback([]);
    callback(
      Object.entries(snap.val())
        .map(([pieceId, p]) => ({ ...p, pieceId }))
        .sort((a, b) => (b.acquiredAt || 0) - (a.acquiredAt || 0))
    );
  });

// ─── LISTINGS — TRUE ESCROW ──────────────────────────────────────────
// Listing a piece MOVES it: out of the seller's gallery, into the
// listing record. While listed, nobody can frame it, gift it, or sell
// it twice — the listing IS its location. Delisting moves it back.
//
// Write order on list: create the listing (escrow copy exists) THEN
// remove from gallery. If the second write fails, the piece is
// momentarily duplicated (recoverable, visible) — never destroyed.
// Silent loss is the enemy; brief duplication is a loud, fixable state.

export const listPiece = async (uid, sellerName, pieceId, price, currency) => {
  // Re-read the rate table fresh. The picker the seller saw may be a
  // few minutes stale, and a currency can be retired mid-session.
  const rates = await getRates();
  const legal = bazaarCurrencies(rates);
  const cur = legal[currency];
  if (!cur) {
    return {
      success: false,
      reason: "That currency isn't registered at the Crest Well.",
    };
  }

  const { min, max } = priceBoundsFor(currency, rates);
  const p = Math.floor(Number(price));
  if (!Number.isFinite(p) || p < min || p > max) {
    return {
      success: false,
      reason: `Price must be ${min.toLocaleString()}–${max.toLocaleString()} ${currency}.`,
    };
  }

  const pieceSnap = await get(ref(db, `profiles/${uid}/gallery/${pieceId}`));
  if (!pieceSnap.exists()) return { success: false, reason: "You don't hold that piece." };
  const piece = pieceSnap.val();

  const mintSnap = await get(ref(db, `bazaar/mints/${piece.artId}`));
  const mint = mintSnap.exists() ? mintSnap.val() : null;
  if (!mint) return { success: false, reason: "This piece has no mint record — it can't be listed." };
  if (!BAZAAR_CATEGORIES[mint.category]?.live) {
    return { success: false, reason: "That category's market isn't open yet." };
  }

  const now = Date.now();
  const listingId = `lst_${newSerial().toLowerCase()}`;

  // 1) Escrow record first (grant-before-flag: the piece's new home
  //    exists before its old home is cleared).
  await set(ref(db, `bazaar/listings/${listingId}`), {
    status: "open",
    seller: uid,
    sellerName: String(sellerName || "Unknown").slice(0, 24),
    price: p,
    currency,
    createdAt: now,
    piece,                       // the escrowed piece, whole
    pieceId,
    // denormalized for the browse grid (mint stays the truth):
    artId: piece.artId,
    title: mint.title,
    image: mint.image,
    category: mint.category,
    creator: mint.creator,           // creator uid — enables creator-gallery links
    creatorName: mint.creatorName,
    editionSize: mint.editionSize,
  });

  // 2) Clear the gallery slot.
  await set(ref(db, `profiles/${uid}/gallery/${pieceId}`), null);

  return { success: true, listingId };
};

export const delistPiece = async (uid, listingId) => {
  // Claim the listing via transaction — only the seller, only if open.
  const statusRef = ref(db, `bazaar/listings/${listingId}/status`);
  const tx = await runTransaction(statusRef, (status) =>
    status === "open" ? "delisted" : undefined // undefined aborts
  );
  if (!tx.committed) return { success: false, reason: "Listing is no longer open." };

  const snap = await get(ref(db, `bazaar/listings/${listingId}`));
  if (!snap.exists()) return { success: false, reason: "Listing vanished." };
  const listing = snap.val();

  if (listing.seller !== uid) {
    // Not yours — undo the claim.
    await set(statusRef, "open");
    return { success: false, reason: "Not your listing." };
  }

  // Return the piece home (grant-before-flag: it's back before we're done).
  await set(ref(db, `profiles/${uid}/gallery/${listing.pieceId}`), listing.piece);
  await update(ref(db, `bazaar/listings/${listingId}`), { delistedAt: Date.now() });

  return { success: true };
};

// ─── THE BUY (the whole point) ───────────────────────────────────────
// Safety ladder, in order:
//   1. Claim the listing:   status open → sold (runTransaction — the
//      lock; two buyers can't both win).
//   2. Charge the buyer:    delta transaction on their currency field;
//      aborts if funds are short. On abort → listing re-opened, no harm.
//   3. Pay the seller:      the FULL asking price, delta transaction.
//                           There is no cut. Pierre takes nothing.
//   4. Deliver the piece:   into the buyer's gallery, provenance
//      "bazaar", carrying who it came from and what was paid.
//   5. Write the ledger:    permanent, public record of the sale.
// Steps 3+ never move backwards — once the buyer is charged, the sale
// is real; later steps are deliveries, retried loudly if they fail.
export const buyPiece = async (uid, buyerName, listingId) => {
  const snap = await get(ref(db, `bazaar/listings/${listingId}`));
  if (!snap.exists()) return { success: false, reason: "Listing not found." };
  const listing = snap.val();

  if (listing.status !== "open") return { success: false, reason: "Already gone." };
  if (listing.seller === uid)    return { success: false, reason: "That's your own stall." };

  // Registry lookup, not rate-table lookup — a listing priced in a
  // since-deactivated currency must still be buyable.
  const field = currencyField(listing.currency);
  const label = CURRENCIES[listing.currency]?.name || listing.currency;
  if (!field) return { success: false, reason: "Listing has a broken currency. Tell caP'n." };

  const price    = listing.price;
  const toSeller = price;   // the whole thing. No cut. Not any more.

  // 1) Claim.
  const statusRef = ref(db, `bazaar/listings/${listingId}/status`);
  const claim = await runTransaction(statusRef, (status) =>
    status === "open" ? "sold" : undefined
  );
  if (!claim.committed) return { success: false, reason: "Someone beat you to it." };

  // 2) Charge the buyer (delta; abort inside the transaction if short).
  //    The buyer must HOLD this currency — no auto-routing through DC.
  const buyerRef = ref(db, `profiles/${uid}/${field}`);
  const charge = await runTransaction(buyerRef, (bal) => {
    const b = bal || 0;
    if (b < price) return undefined; // abort — insufficient funds
    return b - price;
  });
  if (!charge.committed) {
    await set(statusRef, "open"); // release the claim, no money moved
    return { success: false, reason: `Not enough ${label}. Trade for some at the Crest Well.` };
  }

  const now = Date.now();

  // 3) Pay the seller — the full asking price, nothing skimmed.
  // ⚠⚠ THIS IS A CROSS-UID WRITE and therefore the likeliest line in
  // the whole ladder to hit `permission_denied`. Unchecked, a failure
  // here left the buyer charged, the listing marked sold, the piece
  // never delivered and the seller unpaid — with nothing written down
  // anywhere. It cannot be rolled back safely (the claim is already
  // gone), so when it fails the transfer is RECORDED as stuck and the
  // ladder carries on delivering the piece, which is the half that is
  // still recoverable. Found by adversarial review 2026-09-14.
  let sellerPaid = false;
  try {
    const pay = await runTransaction(
      ref(db, `profiles/${listing.seller}/${field}`),
      (bal) => (bal || 0) + toSeller
    );
    sellerPaid = !!pay?.committed;
  } catch { sellerPaid = false; }
  if (!sellerPaid) {
    // ⚠ UNDER `bazaar/`, not a NEW TOP-LEVEL NODE. A new root would be
    // denied by the rules the moment beta ends, the `.catch` would
    // swallow it, and this record — the only trace of a half-finished
    // sale — would silently not exist while the comment promised it did.
    await push(ref(db, "bazaar/stuck"), {
      listingId, pieceId: listing.pieceId, seller: listing.seller, buyer: uid,
      owedField: field, owed: toSeller, at: Date.now(),
      reason: "seller_credit_failed",
    }).catch(() => {});
  }

  // 4) Deliver the piece — provenance updates to "bazaar", history kept.
  await set(ref(db, `profiles/${uid}/gallery/${listing.pieceId}`), {
    ...listing.piece,
    provenance: "bazaar",
    acquiredAt: now,
    from: {
      seller: listing.seller,
      sellerName: listing.sellerName,
      price,
      currency: listing.currency,
    },
  });

  // 5) Seal the listing + the ledger.
  await update(ref(db, `bazaar/listings/${listingId}`), {
    soldTo: uid,
    soldToName: String(buyerName || "Unknown").slice(0, 24),
    soldAt: now,
  });
  await set(ref(db, `bazaar/ledger/${now}_${listingId}`), {
    listingId,
    artId: listing.artId,
    pieceId: listing.pieceId,
    title: listing.title,
    seller: listing.seller,
    buyer: uid,
    price,
    // `cut` stays in the schema so already-written rows keep their shape
    // and old readers don't break. It is 0 forever. Append-only law:
    // we never rewrite a player's history, and we never charge a new one.
    cut: 0,
    currency: listing.currency,
    ts: now,
  });

  return { success: true, price, cut: 0, currency: listing.currency };
};

// ─── BROWSE / LISTEN ─────────────────────────────────────────────────
// Beta-scale: listen to the whole listings node and filter client-side.
// When the Bazaar outgrows this, add .indexOn ["status","category"] and
// query server-side — noted in the roadmap, not needed at current scale.
export const listenToOpenListings = (callback) =>
  onValue(ref(db, "bazaar/listings"), (snap) => {
    if (!snap.exists()) return callback([]);
    callback(
      Object.entries(snap.val())
        .map(([listingId, l]) => ({ ...l, listingId }))
        .filter((l) => l.status === "open")
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    );
  });

export const listenToMyListings = (uid, callback) =>
  onValue(ref(db, "bazaar/listings"), (snap) => {
    if (!snap.exists()) return callback([]);
    callback(
      Object.entries(snap.val())
        .map(([listingId, l]) => ({ ...l, listingId }))
        .filter((l) => l.seller === uid)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    );
  });

export const getMint = async (artId) => {
  const snap = await get(ref(db, `bazaar/mints/${artId}`));
  return snap.exists() ? { ...snap.val(), artId } : null;
};

// Recent sales — the pulse of the market (last 20).
export const getRecentSales = async () => {
  const snap = await get(ref(db, "bazaar/ledger"));
  if (!snap.exists()) return [];
  return Object.values(snap.val())
    .sort((a, b) => (b.ts || 0) - (a.ts || 0))
    .slice(0, 20);
};

// ─── THE MARKET PULSE — live sales feed ──────────────────────────────
// A real-time subscription to the ledger, so the pier's Pulse board and the
// Bazaar's Pulse tab tick the instant a sale closes anywhere in the IE. Same
// beta-scale pattern as the listings listeners: read the whole ledger node,
// sort newest-first, cap the feed. Returns an unsubscribe fn.
export const listenToLedger = (callback, limit = 30) =>
  onValue(ref(db, "bazaar/ledger"), (snap) => {
    if (!snap.exists()) return callback([]);
    callback(
      Object.entries(snap.val())
        .map(([saleId, s]) => ({ ...s, saleId }))
        .sort((a, b) => (b.ts || 0) - (a.ts || 0))
        .slice(0, limit)
    );
  });

// ─── CREATOR GALLERIES — "everything by this creator" ────────────────
// The Bazaar's creator pages: every edition a given uid has ever minted,
// newest first, hydrated with its mint record (title, image, editionSize).
// Reads the whole mints node and filters by creator — beta-scale, mirrors
// the listings pattern; add .indexOn ["creator"] when volume warrants it.
// System mints (creator "pierre", the earned prints) are excluded by default
// so a creator page shows a person's own work, not system grants.
export const listenToCreatorMints = (creatorUid, callback) =>
  onValue(ref(db, "bazaar/mints"), (snap) => {
    if (!snap.exists()) return callback([]);
    callback(
      Object.entries(snap.val())
        .map(([artId, m]) => ({ ...m, artId }))
        .filter((m) => m.creator === creatorUid)
        .sort((a, b) => (b.mintedAt || 0) - (a.mintedAt || 0))
    );
  });

// One-shot version, for contexts that don't want a live subscription.
export const getCreatorMints = async (creatorUid) => {
  const snap = await get(ref(db, "bazaar/mints"));
  if (!snap.exists()) return [];
  return Object.entries(snap.val())
    .map(([artId, m]) => ({ ...m, artId }))
    .filter((m) => m.creator === creatorUid)
    .sort((a, b) => (b.mintedAt || 0) - (a.mintedAt || 0));
};
